<template>
  <b-modal
    :id="modalName"
    size="lg"
    centered
    hide-footer
    title="Selecionar item predefinido de receita"
    @shown="handleShown"
  >
    <div class="presetSearch">
      <b-form-input
        ref="searchInput"
        v-model="query"
        size="sm"
        :disabled="loading"
        placeholder="Pesquisar por produto, cultura ou diagnóstico"
        @keyup.enter="search"
      />
      <b-button
        size="sm"
        class="searchBtn"
        :disabled="loading"
        @click="search"
      >
        <b-icon-search />
      </b-button>
    </div>

    <div v-if="loading" class="presetState">
      <b-spinner small /> {{ loadingMessage }}
    </div>
    <div v-else-if="!items.length" class="presetState">
      Não há itens predefinidos para exibir.
    </div>
    <div v-else class="presetList">
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        class="presetItem"
        @click="selectPreset(item.id)"
      >
        <span class="presetTitle">
          {{ item.infortecnicaReceita.agrotoxico || "Produto não informado" }}
        </span>
        <span>
          Cultura: {{ item.infortecnicaReceita.nomeCultura || "--" }} |
          Diagnóstico:
          {{ item.infortecnicaReceita.nomeProblema || "--" }} | Quantidade:
          {{ item.quantidade }}
        </span>
        <span class="presetPackageDetails">
          <span>
            <strong>Embalagem:</strong>
            {{ item.infortecnicaReceita.embalagem || "--" }}
          </span>
          <span>
            <strong>Lote:</strong>
            {{ item.infortecnicaReceita.lote || "--" }}
          </span>
        </span>
      </button>
    </div>

    <div class="presetFooter">
      <b-button
        size="sm"
        variant="outline-secondary"
        :disabled="page <= 1 || loading"
        @click="changePage(page - 1)"
      >
        <b-icon-chevron-left />
      </b-button>
      <span>Página {{ page }}</span>
      <b-button
        size="sm"
        variant="outline-secondary"
        :disabled="!hasNextPage || loading"
        @click="changePage(page + 1)"
      >
        <b-icon-chevron-right />
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import servicePrescriptionPreset from "../../services/servicePrescriptionPreset";

export default {
  props: {
    modalName: {
      type: String,
      default: "modal-prescription-presets",
    },
  },
  data() {
    return {
      query: "",
      page: 1,
      limit: 10,
      items: [],
      hasNextPage: false,
      loading: false,
      loadingMessage: "Carregando itens...",
    };
  },
  methods: {
    handleShown() {
      this.page = 1;
      this.loadItems();
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    search() {
      this.page = 1;
      this.loadItems();
    },
    changePage(page) {
      this.page = page;
      this.loadItems();
    },
    normalizeList(result) {
      if (Array.isArray(result)) return result;
      if (Array.isArray(result?.data)) return result.data;
      if (Array.isArray(result?.items)) return result.items;
      if (Array.isArray(result?.rows)) return result.rows;
      return [];
    },
    async loadItems() {
      try {
        this.loadingMessage = "Carregando itens...";
        this.loading = true;
        const result = await servicePrescriptionPreset.findAll(
          this.query,
          this.page,
          this.limit
        );
        this.items = this.normalizeList(result).map((item) => ({
          ...item,
          infortecnicaReceita: item.infortecnicaReceita || {},
        }));
        const total = Number(result?.total || result?.count || 0);
        this.hasNextPage = total
          ? this.page * this.limit < total
          : this.items.length === this.limit;
      } catch (error) {
        this.items = [];
        this.$toast.error(
          error?.response?.data?.message ||
            "Não foi possível carregar os itens predefinidos."
        );
      } finally {
        this.loading = false;
      }
    },
    async selectPreset(id) {
      let selected = false;
      try {
        if (this.loading) return;
        this.loadingMessage = "Carregando item predefinido...";
        this.loading = true;
        this.$emit("loading-change", true);
        const preset = await servicePrescriptionPreset.findById(id);
        selected = true;
        this.$emit("selected", preset);
        this.$bvModal.hide(this.modalName);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message ||
            "Não foi possível carregar o item predefinido."
        );
      } finally {
        this.loading = false;
        if (!selected) this.$emit("loading-change", false);
      }
    },
  },
};
</script>

<style scoped>
.presetSearch {
  display: flex;
  margin-bottom: 1rem;
}

.presetSearch input {
  border-radius: 4px 0 0 4px;
}

.searchBtn {
  background: #006256;
  border-color: #006256;
  border-radius: 0 4px 4px 0;
  width: 42px;
}

.presetList {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 420px;
  overflow: auto;
}

.presetItem {
  background: #f8f8f8;
  border: 1px solid #c8dfd5;
  border-left: 6px solid #006256;
  border-radius: 4px;
  color: #5c5855;
  display: flex;
  flex-direction: column;
  font-family: Poppins-Regular, sans-serif !important;
  font-size: 12px;
  padding: 0.65rem 0.8rem;
  text-align: left;
}

.presetItem:hover {
  background: #edf7f4;
}

.presetTitle {
  color: #006256;
  font-family: Poppins-Medium, sans-serif !important;
  font-size: 13px;
}

.presetPackageDetails {
  color: #465954;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
  margin-top: 0.25rem;
}

.presetPackageDetails strong {
  color: #006256;
  font-family: Poppins-Medium, sans-serif !important;
}

.presetState {
  color: #6c757d;
  padding: 2rem;
  text-align: center;
}

.presetFooter {
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 0.75rem;
  justify-content: center;
  padding-top: 1rem;
}
</style>
